import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import DocumentPreview, { isPreviewable } from "./DocumentPreview";
import { MONTHS_SHORT } from "../lib/months";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

const DocumentsSection = ({ account, files, onFilesChange, enableStatementFiling = false, enableInvoiceFiling = false }) => {
  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [addingFolderFor, setAddingFolderFor] = useState(null); // "root" | parent folder id | null
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renameFileValue, setRenameFileValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts/${account.id}/folders`);
      setFolders(res.data);
    } catch (error) {
      console.error("Failed to fetch folders:", error);
    }
  }, [account.id]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const folderIds = new Set(folders.map((f) => f.id));
  const topFolders = folders.filter((f) => !f.parent_id || !folderIds.has(f.parent_id));
  const childrenOf = (id) => folders.filter((f) => f.parent_id === id);
  const rootFiles = files.filter((f) => !f.folder_id || !folderIds.has(f.folder_id));
  const filesIn = (id) => files.filter((f) => f.folder_id === id);
  const descendantFileCount = (folder) =>
    filesIn(folder.id).length + childrenOf(folder.id).reduce((n, c) => n + filesIn(c.id).length, 0);

  const notify = async () => {
    if (onFilesChange) await onFilesChange();
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    const parentId = addingFolderFor === "root" ? null : addingFolderFor;
    try {
      await axios.post(`${API}/accounts/${account.id}/folders`, { name, parent_id: parentId });
      setNewFolderName("");
      setAddingFolderFor(null);
      if (parentId) setExpanded((ex) => ({ ...ex, [parentId]: true }));
      await fetchFolders();
      toast.success(`Folder "${name}" created`);
    } catch (error) {
      console.error("Failed to create folder:", error);
      toast.error(error.response?.data?.detail || "Failed to create folder");
    }
  };

  const renameFolder = async (folder) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name || name === folder.name) return;
    try {
      await axios.put(`${API}/folders/${folder.id}`, { name });
      await fetchFolders();
    } catch (error) {
      console.error("Failed to rename folder:", error);
      toast.error("Failed to rename folder");
    }
  };

  const deleteFolder = async (folder, fileAction) => {
    try {
      await axios.delete(`${API}/folders/${folder.id}?file_action=${fileAction}`);
      setDeleteTarget(null);
      await fetchFolders();
      await notify();
      toast.success(fileAction === "delete" ? "Folder and its files deleted" : "Folder deleted — files kept");
    } catch (error) {
      console.error("Failed to delete folder:", error);
      toast.error("Failed to delete folder");
    }
  };

  const onDeleteFolderClick = (folder) => {
    const subCount = childrenOf(folder.id).length;
    if (descendantFileCount(folder) === 0) {
      const msg =
        subCount > 0
          ? `Delete folder "${folder.name}" and its ${subCount} subfolder${subCount === 1 ? "" : "s"}?`
          : `Delete folder "${folder.name}"?`;
      if (window.confirm(msg)) deleteFolder(folder, "keep");
    } else {
      setDeleteTarget(folder);
    }
  };

  const moveFile = async (fileId, folderId) => {
    try {
      const fd = new FormData();
      fd.append("folder_id", folderId || "__clear__");
      const res = await axios.put(`${API}/files/${fileId}`, fd, MULTIPART);
      if ((res.data.folder_id || null) !== (folderId || null)) {
        toast.error("Failed to move file");
        return;
      }
      await notify();
    } catch (error) {
      console.error("Failed to move file:", error);
      toast.error("Failed to move file");
    }
  };

  const moveFolder = async (folderId, parentId) => {
    try {
      await axios.put(`${API}/folders/${folderId}`, { parent_id: parentId });
      await fetchFolders();
    } catch (error) {
      console.error("Failed to move folder:", error);
      toast.error(error.response?.data?.detail || "Failed to move folder");
    }
  };

  const handleDrop = (target) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    const dest = target === "root" ? null : target;

    const fileId = e.dataTransfer.getData("text/file-id");
    if (fileId) {
      const file = files.find((f) => f.id === fileId);
      const current = file && folderIds.has(file.folder_id) ? file.folder_id : null;
      if (current === dest) return;
      await moveFile(fileId, dest);
      if (dest) setExpanded((ex) => ({ ...ex, [dest]: true }));
      return;
    }

    const draggedFolderId = e.dataTransfer.getData("text/folder-id");
    if (draggedFolderId) {
      const dragged = folders.find((f) => f.id === draggedFolderId);
      if (!dragged || dest === draggedFolderId) return;
      if ((dragged.parent_id || null) === dest) return;
      if (dest) {
        const destFolder = folders.find((f) => f.id === dest);
        if (!destFolder || destFolder.parent_id) {
          toast.error("Folders can only be nested two levels deep");
          return;
        }
        if (childrenOf(draggedFolderId).length > 0) {
          toast.error("Move its subfolders out first");
          return;
        }
      }
      await moveFolder(draggedFolderId, dest);
      if (dest) setExpanded((ex) => ({ ...ex, [dest]: true }));
    }
  };

  const dropProps = (target) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverTarget(target);
    },
    onDragLeave: (e) => {
      e.stopPropagation();
      setDragOverTarget((t) => (t === target ? null : t));
    },
    onDrop: handleDrop(target),
  });

  const downloadFile = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file:", error);
      toast.error("Failed to download file.");
    }
  };

  const deleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.original_filename}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/files/${file.id}`);
      await notify();
      toast.success("Document deleted");
    } catch (error) {
      console.error("Failed to delete file:", error);
      toast.error("Failed to delete document.");
    }
  };

  const renameFile = async (file) => {
    const name = renameFileValue.trim();
    setRenamingFileId(null);
    if (!name || name === file.original_filename) return;
    try {
      const fd = new FormData();
      fd.append("original_filename", name);
      await axios.put(`${API}/files/${file.id}`, fd, MULTIPART);
      await notify();
      toast.success("Filename updated");
    } catch (error) {
      console.error("Failed to rename file:", error);
      toast.error(error.response?.data?.detail || "Failed to rename file");
    }
  };

  const assignToStatement = async (file, value) => {
    if (!value) return;
    const [y, m] = value.split("-").map((n) => parseInt(n, 10));
    try {
      const res = await axios.post(`${API}/accounts/${account.id}/statements`, { year: y, month: m });
      const fd = new FormData();
      fd.append("statement_id", res.data.id);
      await axios.put(`${API}/files/${file.id}`, fd, MULTIPART);
      toast.success(`Filed under ${MONTHS_SHORT[m - 1]} ${y}`);
      await notify();
    } catch (error) {
      console.error("Failed to file document:", error);
      toast.error("Failed to file document");
    }
  };

  const [sendingToInvoice, setSendingToInvoice] = useState(null);

  const sendToInvoice = async (file) => {
    setSendingToInvoice(file.id);
    try {
      const inv = (await axios.post(`${API}/accounts/${account.id}/invoices`, {})).data;
      const fd = new FormData();
      fd.append("invoice_id", inv.id);
      await axios.put(`${API}/files/${file.id}`, fd, MULTIPART);
      const mime = file.file_type || "";
      const extractable = mime === "application/pdf" || mime.startsWith("image/") || mime.startsWith("text/");
      if (extractable) {
        try {
          await axios.post(`${API}/invoices/${inv.id}/extract`, { file_id: file.id });
          toast.success("Invoice created — fields extracted from the document");
        } catch {
          toast.warning("Invoice created and document attached, but extraction failed");
        }
      } else {
        toast.success("Invoice created with document attached");
      }
      await notify();
    } catch (error) {
      console.error("Failed to create invoice from document:", error);
      toast.error("Failed to create invoice from document");
    } finally {
      setSendingToInvoice(null);
    }
  };

  const filingOptions = [];
  if (enableStatementFiling) {
    const thisYear = new Date().getFullYear();
    [thisYear, thisYear - 1].forEach((y) =>
      MONTHS_SHORT.forEach((mn, i) => filingOptions.push({ value: `${y}-${i + 1}`, label: `${mn} ${y}` }))
    );
  }

  const newFolderInput = (
    <div className="flex items-center gap-1.5">
      <input
        autoFocus
        value={newFolderName}
        onChange={(e) => setNewFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") createFolder();
          if (e.key === "Escape") {
            setAddingFolderFor(null);
            setNewFolderName("");
          }
        }}
        placeholder="Folder name"
        className="border border-gray-300 rounded-md px-2 py-1 text-xs w-40"
        data-testid="new-folder-name-input"
      />
      <button
        onClick={createFolder}
        data-testid="new-folder-create"
        className="text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md px-2 py-1"
      >
        Create
      </button>
      <button
        onClick={() => {
          setAddingFolderFor(null);
          setNewFolderName("");
        }}
        data-testid="new-folder-cancel"
        className="text-xs text-gray-500 hover:text-gray-700 px-1"
      >
        ✕
      </button>
    </div>
  );

  const renderFile = (file) => (
    <div
      key={file.id}
      draggable={renamingFileId !== file.id}
      onDragStart={(e) => {
        if (renamingFileId === file.id) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData("text/file-id", file.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-white cursor-grab active:cursor-grabbing hover:border-gray-300"
      data-testid={`document-${file.id}`}
    >
      <div className="min-w-0 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div className="min-w-0 flex-1">
          {renamingFileId === file.id ? (
            <input
              autoFocus
              value={renameFileValue}
              onChange={(e) => setRenameFileValue(e.target.value)}
              onBlur={() => renameFile(file)}
              onKeyDown={(e) => {
                if (e.key === "Enter") renameFile(file);
                if (e.key === "Escape") setRenamingFileId(null);
              }}
              onClick={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
              className="border border-gray-300 rounded-md px-1.5 py-0.5 text-sm w-full max-w-xs"
              data-testid={`file-rename-input-${file.id}`}
            />
          ) : (
            <div className="text-sm font-medium text-gray-900 truncate" data-testid={`file-name-${file.id}`}>
              {file.original_filename}
            </div>
          )}
          <div className="text-xs text-gray-500">
            {formatFileSize(file.file_size)} • {new Date(file.uploaded_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {enableInvoiceFiling && (
          <button
            onClick={() => sendToInvoice(file)}
            disabled={sendingToInvoice === file.id}
            data-testid={`file-to-invoice-${file.id}`}
            title="Create an invoice from this document (auto-extracts fields from PDFs and images)"
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-md px-2 py-1 hover:bg-indigo-50 disabled:opacity-50"
          >
            {sendingToInvoice === file.id ? "Creating…" : "→ Invoice"}
          </button>
        )}
        {enableStatementFiling && (
          <select
            data-testid={`assign-month-${file.id}`}
            defaultValue=""
            onChange={(e) => assignToStatement(file, e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
          >
            <option value="" disabled>File under…</option>
            {filingOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        {isPreviewable(file) && (
          <button
            onClick={() => setPreviewFile(file)}
            data-testid={`preview-file-${file.id}`}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50"
          >
            Preview
          </button>
        )}
        <button
          onClick={() => downloadFile(file)}
          data-testid={`download-file-${file.id}`}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50"
        >
          Download
        </button>
        <button
          onClick={() => {
            setRenamingFileId(file.id);
            setRenameFileValue(file.original_filename);
          }}
          data-testid={`rename-file-${file.id}`}
          title="Rename file"
          className="text-gray-500 hover:text-gray-800 border border-gray-200 rounded-md px-1.5 py-1 hover:bg-gray-50 inline-flex items-center"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={() => deleteFile(file)}
          data-testid={`delete-file-${file.id}`}
          title="Delete document"
          className="text-red-500 hover:text-red-700 border border-red-200 rounded-md px-1.5 py-1 hover:bg-red-50 inline-flex items-center"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
          </svg>
        </button>
      </div>
    </div>
  );

  const renderFolder = (folder, depth = 0) => {
    const inFolder = filesIn(folder.id);
    const subfolders = depth === 0 ? childrenOf(folder.id) : [];
    const isOpen = !!expanded[folder.id];
    const isOver = dragOverTarget === folder.id;
    return (
      <div
        key={folder.id}
        {...dropProps(folder.id)}
        className={`border rounded-lg transition-colors ${
          isOver ? "border-blue-400 bg-blue-50 ring-1 ring-blue-200" : depth === 0 ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-white"
        }`}
        data-testid={`folder-${folder.id}`}
      >
        <div
          className="flex items-center justify-between px-3 py-2"
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.setData("text/folder-id", folder.id);
            e.dataTransfer.effectAllowed = "move";
          }}
        >
          <button
            onClick={() => setExpanded((ex) => ({ ...ex, [folder.id]: !ex[folder.id] }))}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            data-testid={`folder-toggle-${folder.id}`}
          >
            <svg
              className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <svg className={`w-4 h-4 flex-shrink-0 ${depth === 0 ? "text-amber-500" : "text-amber-400"}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" />
            </svg>
            {renamingId === folder.id ? (
              <input
                autoFocus
                value={renameValue}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => renameFolder(folder)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") renameFolder(folder);
                  if (e.key === "Escape") setRenamingId(null);
                }}
                className="border border-gray-300 rounded-md px-1.5 py-0.5 text-sm w-40"
                data-testid={`folder-rename-input-${folder.id}`}
              />
            ) : (
              <span className="text-sm font-medium text-gray-900 truncate" data-testid={`folder-name-${folder.id}`}>
                {folder.name}
              </span>
            )}
            <span className="text-xs text-gray-500 flex-shrink-0" data-testid={`folder-count-${folder.id}`}>
              {(() => {
                const total = depth === 0 ? descendantFileCount(folder) : inFolder.length;
                return `${total} file${total === 1 ? "" : "s"}`;
              })()}
              {subfolders.length > 0 && ` · ${subfolders.length} subfolder${subfolders.length === 1 ? "" : "s"}`}
            </span>
          </button>
          <div className="flex items-center gap-1 flex-shrink-0">
            {depth === 0 && (
              <button
                onClick={() => {
                  setAddingFolderFor(folder.id);
                  setNewFolderName("");
                  setExpanded((ex) => ({ ...ex, [folder.id]: true }));
                }}
                title="Add subfolder"
                data-testid={`folder-add-sub-${folder.id}`}
                className="text-gray-400 hover:text-gray-700 p-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m-3-3h6M3 6a2 2 0 012-2h5l2 2h7a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
                </svg>
              </button>
            )}
            <button
              onClick={() => {
                setRenamingId(folder.id);
                setRenameValue(folder.name);
              }}
              title="Rename folder"
              data-testid={`folder-rename-${folder.id}`}
              className="text-gray-400 hover:text-gray-700 p-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDeleteFolderClick(folder)}
              title="Delete folder"
              data-testid={`folder-delete-${folder.id}`}
              className="text-red-400 hover:text-red-600 p-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
              </svg>
            </button>
          </div>
        </div>
        {isOpen && (
          <div className="px-3 pb-2 space-y-1.5" data-testid={`folder-files-${folder.id}`}>
            {addingFolderFor === folder.id && <div className="pt-0.5">{newFolderInput}</div>}
            {subfolders.map((sub) => renderFolder(sub, depth + 1))}
            {inFolder.length === 0 && subfolders.length === 0 && addingFolderFor !== folder.id ? (
              <div className="text-xs text-gray-400 italic px-1 pb-1">Empty — drag files here</div>
            ) : (
              inFolder.map(renderFile)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="documents-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900" data-testid="documents-heading">
          Documents ({files.length})
        </h3>
        {addingFolderFor === "root" ? (
          newFolderInput
        ) : (
          <button
            onClick={() => {
              setAddingFolderFor("root");
              setNewFolderName("");
            }}
            data-testid="new-folder-button"
            className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50"
          >
            + New folder
          </button>
        )}
      </div>

      {topFolders.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid="folders-list">
          {topFolders.map((folder) => renderFolder(folder, 0))}
        </div>
      )}

      <div
        {...dropProps("root")}
        className={`rounded-lg transition-colors ${
          dragOverTarget === "root" ? "ring-2 ring-blue-300 bg-blue-50/50 p-1 -m-1" : ""
        }`}
        data-testid="documents-root-dropzone"
      >
        {rootFiles.length === 0 && topFolders.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm" data-testid="empty-documents">
            No documents uploaded yet
          </div>
        ) : rootFiles.length === 0 ? (
          <div className="text-center py-2.5 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
            Drag a file or folder here to move it to the top level
          </div>
        ) : (
          <div className="space-y-1.5">{rootFiles.map(renderFile)}</div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="folder-delete-modal">
          <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-sm mx-4">
            <h4 className="text-sm font-semibold text-gray-900">Delete folder "{deleteTarget.name}"?</h4>
            <p className="text-xs text-gray-600 mt-1">
              It contains {descendantFileCount(deleteTarget)} file{descendantFileCount(deleteTarget) === 1 ? "" : "s"}
              {childrenOf(deleteTarget.id).length > 0 &&
                ` (including ${childrenOf(deleteTarget.id).length} subfolder${childrenOf(deleteTarget.id).length === 1 ? "" : "s"})`}
              . What should happen to them?
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={() => deleteFolder(deleteTarget, "keep")}
                data-testid="folder-delete-keep-files"
                className="text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-2"
              >
                Keep files — move them to Documents
              </button>
              <button
                onClick={() => deleteFolder(deleteTarget, "delete")}
                data-testid="folder-delete-delete-files"
                className="text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-md px-3 py-2"
              >
                Delete the files too
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                data-testid="folder-delete-cancel"
                className="text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {previewFile && <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

export default DocumentsSection;
